'use client';

import { Container } from '@/components/layout/container/container';
import { Badge } from '@/components/ui/badge/badge';
import { DecorativeLines } from '@/components/ui/decorative-lines';
import { Image } from '@/components/ui/image/image';
import { Media } from '@/components/ui/media/media';
import RichTextViewer from '@/components/ui/rich-text-viewer/rich-text-viewer';
import type { MediaData } from '@/lib/getMediaData';
import type { SectionBgVariant } from '@/lib/types/misc';
import { cn } from '@/lib/utils';
import { Document } from '@contentful/rich-text-types';
import { motion } from 'motion/react';
import { HTMLAttributes, ReactNode, useEffect, useRef } from 'react';
import styles from './information-with-benefits-image.module.scss';

type UniversalCard = {
  id: string;
  title: string;
  description?: Document;
  icon?: string | null;
  iconAltText?: string | null;
  iconPosition?: 'inline' | 'top';
};

type InformationWithBenefitsImageProps = {
  badge?: string;
  title: string;
  description?: Document;
  backgroundVariant: SectionBgVariant;
  backgroundImage: string | null;
  backgroundColorFrom?: string;
  isBackgroundDark?: boolean;
  showDecorativeLines?: boolean;
  showDecorativeDots?: boolean;
  cards: UniversalCard[];
  cardDisplayStyle?: 'cards' | 'checklist';
  sideImage: string | null;
  sideImageAlt: string | null;
  sideMediaData?: MediaData | null;
  imageAspectRatio?: '1:1' | '4:3' | '16:9' | 'original';
  imageAlignment?: 'top' | 'middle' | 'bottom';
  mediaPosition?: 'left' | 'right';
  columnLayout?: 'equal';
  cta?: ReactNode;
  headingAlignment?: 'left' | 'center' | 'right';
  columns?: '2-column' | '3-column';
  ctaBanner?: {
    trustPilotHtml?: string;
    content?: Document;
    link?: ReactNode;
    backgroundVariant?: SectionBgVariant;
    backgroundImage?: string;
    backgroundColorFrom?: string;
    isBackgroundDark?: boolean;
  };
  sectionId: string;
} & HTMLAttributes<HTMLElement>;

const CheckCircleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="12" fill="#887EAD" />
    <path
      d="M7.5 12L10.5 15L16.5 9"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CtaBanner = ({
  trustPilotHtml,
  content,
  link,
  backgroundVariant = 'white',
  backgroundImage,
  backgroundColorFrom,
  isBackgroundDark = false,
}: {
  trustPilotHtml?: string;
  content?: Document;
  link?: ReactNode;
  backgroundVariant?: SectionBgVariant;
  backgroundImage?: string;
  backgroundColorFrom?: string;
  isBackgroundDark?: boolean;
}) => {
  const tpRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!tpRef.current || !trustPilotHtml) return;
    tpRef.current.innerHTML = trustPilotHtml;
    const widget = tpRef.current.querySelector('.trustpilot-widget');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tp = (window as any).Trustpilot;
    if (widget && tp) {
      tp.loadFromElement(widget, true);
    }
  }, [trustPilotHtml]);

  return (
    <div
      className={cn(styles.cta_banner, styles[`cta_banner--${backgroundVariant}`])}
      style={
        backgroundVariant === 'custom' && backgroundColorFrom
          ? { background: backgroundColorFrom }
          : undefined
      }
    >
      {backgroundVariant === 'image' && backgroundImage && (
        <div className={styles.cta_banner_bg}>
          <Image src={backgroundImage} alt="" fill={true} className={styles.cta_banner_bg_image} />
        </div>
      )}
      {trustPilotHtml && <div ref={tpRef} suppressHydrationWarning />}
      {content && (
        <div
          className={cn(
            styles.cta_banner_content,
            isBackgroundDark && styles['cta_banner_content--dark'],
          )}
        >
          <RichTextViewer content={content} dark={isBackgroundDark} />
        </div>
      )}
      {link && <div className={styles.cta_banner_link}>{link}</div>}
    </div>
  );
};

export const InformationWithBenefitsImage = ({
  badge = '',
  title,
  description,
  backgroundVariant = 'white',
  backgroundImage,
  backgroundColorFrom,
  isBackgroundDark = false,
  showDecorativeLines = false,
  showDecorativeDots = false,
  cards = [],
  cardDisplayStyle = 'cards',
  sideImage,
  sideImageAlt,
  sideMediaData,
  imageAspectRatio,
  imageAlignment,
  mediaPosition = 'right',
  columnLayout,
  cta,
  headingAlignment = 'left',
  columns = '2-column',
  ctaBanner,
  sectionId,
  className,
  ...props
}: InformationWithBenefitsImageProps) => {
  const hasMedia = !!(sideMediaData || sideImage);
  const isThreeColumn = columns === '3-column';
  const alignmentClass =
    headingAlignment === 'center'
      ? styles['header--center']
      : headingAlignment === 'right'
        ? styles['header--right']
        : undefined;

  const cardsGridClass =
    cardDisplayStyle === 'checklist'
      ? styles.checklist_content
      : isThreeColumn
        ? styles['cards_content--three']
        : styles.cards_content;

  // In 3-column mode, image moves to the header area (not the side column).
  // Also use the side-by-side header layout when there are no cards — the header
  // text sits beside the media instead of stacking above an empty column.
  const noCards = cards.length === 0;
  const showHeaderMedia = hasMedia && headingAlignment !== 'center' && (isThreeColumn || noCards);
  const showSideMedia = hasMedia && !isThreeColumn && !noCards;

  return (
    <section
      className={cn(styles.component, styles[`component--bg-${backgroundVariant}`], className)}
      id={sectionId}
      style={
        backgroundVariant === 'custom' && backgroundColorFrom
          ? { background: backgroundColorFrom }
          : undefined
      }
      {...props}
    >
      {(showDecorativeLines || showDecorativeDots) && (
        <DecorativeLines
          showDots={showDecorativeDots}
          showLines={showDecorativeLines}
          variant={isBackgroundDark ? 'dark' : 'light'}
        />
      )}

      {backgroundVariant === 'image' && backgroundImage && (
        <div className={styles.component_background}>
          <Image
            src={backgroundImage}
            alt="Information with benefits image background"
            className={styles.component_background_image}
            fill={true}
          />
        </div>
      )}

      {backgroundVariant === 'diagonal-line' && (
        <div className={styles.component_diagonal_overlay} aria-hidden="true" />
      )}

      <Container className={styles.component_container}>
        {/* Header — always full-width on top */}
        {showHeaderMedia ? (
          /* 3-column with image: heading + image side by side */
          <div
            className={cn(
              styles.header_row,
              (headingAlignment === 'right' || (noCards && mediaPosition === 'left')) &&
                styles['header_row--reverse'],
            )}
          >
            <div className={cn(styles.header, alignmentClass)}>
              {badge && <Badge text={badge} />}
              <div className={styles.text_container}>
                <h2
                  className={cn(
                    styles.title,
                    styles[`title--bg-${isBackgroundDark ? 'dark' : 'light'}`],
                  )}
                >
                  {title}
                </h2>
                {description && (
                  <div
                    className={cn(
                      styles.description,
                      styles[`description--bg-${isBackgroundDark ? 'dark' : 'light'}`],
                    )}
                  >
                    <RichTextViewer content={description} dark={isBackgroundDark} />
                  </div>
                )}
              </div>
              {cta && <div className={styles.cta}>{cta}</div>}
            </div>
            {hasMedia && (
              <div
                className={cn(
                  styles.header_media,
                  columnLayout === 'equal' && styles['header_media--equal'],
                )}
              >
                {sideMediaData ? (
                  <Media {...sideMediaData} width={0} height={0} />
                ) : (
                  sideImage && (
                    <Image
                      src={sideImage}
                      alt={sideImageAlt || 'illustration'}
                      width={0}
                      height={0}
                      sizes="(max-width: 1024px) 100vw, 40vw"
                      style={{
                        width: '100%',
                        height: 'auto',
                        aspectRatio:
                          imageAspectRatio && imageAspectRatio !== 'original'
                            ? imageAspectRatio.replace(':', ' / ')
                            : undefined,
                        objectFit:
                          imageAspectRatio && imageAspectRatio !== 'original' ? 'cover' : undefined,
                      }}
                    />
                  )
                )}
              </div>
            )}
          </div>
        ) : (
          /* Default: full-width heading only */
          <div className={cn(styles.header, alignmentClass)}>
            {badge && <Badge text={badge} />}
            <div className={styles.text_container}>
              <h2
                className={cn(
                  styles.title,
                  styles[`title--bg-${isBackgroundDark ? 'dark' : 'light'}`],
                )}
              >
                {title}
              </h2>
              {description && (
                <div
                  className={cn(
                    styles.description,
                    styles[`description--bg-${isBackgroundDark ? 'dark' : 'light'}`],
                  )}
                >
                  <RichTextViewer content={description} dark={isBackgroundDark} />
                </div>
              )}
            </div>
            {cta && <div className={styles.cta}>{cta}</div>}
          </div>
        )}

        {/* Content: cards grid + optional side media */}
        {(cards.length > 0 || showSideMedia) && (
          <div
            className={cn(
              styles.columns,
              !isThreeColumn && mediaPosition === 'left' && styles['columns--media-left'],
            )}
          >
            {cards.length > 0 && (
              <div className={styles.left_column}>
                <motion.div
                  className={cardsGridClass}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: [0.04, 0.62, 0.23, 0.98] }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  {cardDisplayStyle === 'checklist' ? (
                    <ul>
                      {cards.map((card) => (
                        <li
                          key={card.id}
                          className={cn(
                            styles.checklist_item,
                            styles[`checklist_item--bg-${isBackgroundDark ? 'dark' : 'light'}`],
                          )}
                        >
                          <span className={styles.checklist_icon}>
                            {card.icon ? (
                              <Image
                                src={card.icon}
                                alt={card.iconAltText ?? card.title}
                                width={24}
                                height={24}
                              />
                            ) : (
                              <CheckCircleIcon />
                            )}
                          </span>
                          {card.description ? (
                            <RichTextViewer content={card.description} dark={isBackgroundDark} />
                          ) : (
                            <span>{card.title}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    cards.map((card) => (
                      <div
                        key={card.id}
                        className={cn(
                          styles.card,
                          styles[
                            card.iconPosition === 'top' ? 'card--icon-column' : 'card--icon-row'
                          ],
                        )}
                      >
                        <div className={styles.card_header}>
                          {card.icon && (
                            <div
                              className={cn(
                                styles.card_icon,
                                card.iconPosition === 'top' ? 'top' : '',
                              )}
                            >
                              <Image
                                src={card.icon}
                                alt={card.iconAltText ?? card.title}
                                width={card.iconPosition === 'top' ? 80 : 40}
                                height={card.iconPosition === 'top' ? 80 : 40}
                              />
                            </div>
                          )}
                          <p className={styles.card_title}>{card.title}</p>
                        </div>
                        {card.description && (
                          <div className={styles.card_description}>
                            <RichTextViewer content={card.description} dark={isBackgroundDark} />
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </motion.div>
              </div>
            )}

            {/* Side media column (2-column only — in 3-column, image is in header) */}
            {showSideMedia && (
              <div
                className={cn(
                  styles.side_media,
                  imageAlignment === 'top'
                    ? styles['side_media--top']
                    : imageAlignment === 'bottom'
                      ? styles['side_media--bottom']
                      : undefined,
                  (columnLayout === 'equal' || cards.length === 0) && styles['side_media--equal'],
                )}
              >
                {sideMediaData ? (
                  <Media {...sideMediaData} width={0} height={0} />
                ) : (
                  sideImage && (
                    <Image
                      src={sideImage}
                      alt={sideImageAlt || 'illustration'}
                      width={0}
                      height={0}
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      style={{
                        width: '100%',
                        height: 'auto',
                        aspectRatio:
                          imageAspectRatio && imageAspectRatio !== 'original'
                            ? imageAspectRatio.replace(':', ' / ')
                            : undefined,
                        objectFit:
                          imageAspectRatio && imageAspectRatio !== 'original' ? 'cover' : undefined,
                      }}
                    />
                  )
                )}
              </div>
            )}
          </div>
        )}

        {ctaBanner && <CtaBanner {...ctaBanner} />}
      </Container>
    </section>
  );
};
